<template>
  <AppContainer :is-loading>
    <div>
      <EPageTitleMobile :title="t(useTranslationPath(callerPath, 'title'))" />

      <EForm>
        <EFormField
          id="method"
          :label="t(useTranslationPath(callerPath, 'methodLabel'))"
          :errors="getFieldErrors(errors, 'method')"
        >
          <ESelect
            id="method"
            v-model="v$.method.$model"
            name="method"
            :options="availableMethods"
            :placeholder="t(useTranslationPath(callerPath, 'defaultSelectPlaceholder'))"
          />
        </EFormField>

        <div
          v-if="hasPendingMethod"
          class="bg-warning-hint text-warning-hint-foreground rounded-md p-3 text-sm"
        >
          {{ t(useTranslationPath(callerPath, 'existingMethodWarning')) }}
        </div>

        <EFormField
          v-if="isEmailMethod"
          id="email"
          :label="t(useTranslationPath(callerPath, 'emailLabel'))"
          :errors="getFieldErrors(errors, 'email')"
        >
          <EInput id="email" v-model="v$.email.$model" name="email" />
        </EFormField>

        <EFormField
          v-if="isMobileMethod"
          id="mobileCountry"
          :label="t(useTranslationPath(callerPath, 'mobileCountryLabel'))"
          :hint="t(useTranslationPath(callerPath, 'mobileCountryHint'))"
          :errors="getFieldErrors(errors, 'mobileCountry')"
        >
          <ESelect
            id="mobileCountry"
            v-model="v$.mobileCountry.$model"
            name="mobileCountry"
            :options="mobileCountryOptions"
            :placeholder="t(useTranslationPath(callerPath, 'defaultSelectPlaceholder'))"
            allow-search
          />
        </EFormField>

        <EFormField
          v-if="isMobileMethod"
          id="mobile"
          :label="t(useTranslationPath(callerPath, 'mobileLabel'))"
          :hint="t(useTranslationPath(callerPath, 'mobileHint'))"
          :errors="getFieldErrors(errors, 'mobile')"
        >
          <EInput id="mobile" v-model="v$.mobile.$model" name="mobile" />
        </EFormField>
      </EForm>
    </div>

    <template #bottom>
      <EButton
        :label="t(useTranslationPath(callerPath, 'add'))"
        block
        :disabled="v$.$invalid"
        @click="onSubmit"
      />
    </template>
  </AppContainer>
</template>

<script setup>
import AppContainer from '@/components/AppContainer.vue'
import { useApiErrorHandler } from '@/composables/useApiErrorHandler.js'
import { useGetVerificationMethods } from '@/composables/useGetConfigVar.js'
import { useTranslationPath } from '@/composables/useTranslationPath.js'
import { useAppStore } from '@/stores/AppStore.js'
import { useUnauthenticatedTokenStore } from '@/stores/unauthenticated/UnauthenticatedTokenStore.js'
import { EButton, EForm, EFormField, EInput, EPageTitleMobile, ESelect } from '@expansivefm/eds'
import useVuelidate from '@vuelidate/core'
import { email, helpers, required, requiredIf } from '@vuelidate/validators'
import { parsePhoneNumberFromString } from 'libphonenumber-js'
import { computed, inject, onMounted, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

const { t } = useI18n()
const $apiUnauthenticatedRequest = inject('$apiUnauthenticatedRequest')
const router = useRouter()
const unauthenticatedTokenStore = useUnauthenticatedTokenStore()
const appStore = useAppStore()
const callerPath = 'views.unauthenticated.verification.methods.add'
const { handleErrors, getFieldErrors } = useApiErrorHandler(router)

const isLoading = ref(null)
const user = ref(null)
const countries = ref(null)
const errors = ref([])

const form = reactive({
  method: null,
  mobileCountry: null,
  mobile: null,
  email: null,
})

const validationRules = {
  method: { required },
  mobileCountry: { requiredIf: requiredIf(() => isMobileMethod.value) },
  mobile: {
    requiredIf: requiredIf(() => isMobileMethod.value),
    validMobileNumber: helpers.withMessage('Please enter a valid mobile number.', (value, vm) => {
      if (!isMobileMethod.value) {
        return true
      }

      const country = vm?.mobileCountry

      if (!country || !value) {
        return false
      }

      const phoneNumber = parsePhoneNumberFromString(value, country)

      return phoneNumber?.isValid() ?? false
    }),
  },
  email: {
    requiredIf: requiredIf(() => isEmailMethod.value),
    email: function (value) {
      return isMobileMethod.value || email.$validator(value)
    },
  },
}

const v$ = useVuelidate(validationRules, form)

onMounted(async () => {
  appStore.showBackButton = false
  isLoading.value = true

  await fetchUser()
  await fetchCountries()

  isLoading.value = false
  appStore.showBackButton = true
})

const fetchUser = async () => {
  const userId = unauthenticatedTokenStore.engineerCheckInAccessToken?.user?.id

  try {
    return await $apiUnauthenticatedRequest
      .get(
        `users`,
        $apiUnauthenticatedRequest.configs.get
          .setId(userId)
          .setIncludes(['userPendingVerificationMethods']),
      )
      .then(({ data }) => {
        user.value = data
      })
  } catch (error) {
    await handleErrors(error)
  }
}

const fetchCountries = async () => {
  try {
    return await $apiUnauthenticatedRequest
      .index(`countries`, $apiUnauthenticatedRequest.configs.index)
      .then(({ data }) => {
        countries.value = data
      })
  } catch (error) {
    await handleErrors(error)
  }
}

const availableMethods = computed(() => {
  const featureFlagMethods = useGetVerificationMethods()
  const methods = []

  if (featureFlagMethods.includes('EMAIL') && !user.value?.email) {
    methods.push({
      label: 'Email',
      value: 'EMAIL',
    })
  }

  if (featureFlagMethods.includes('SMS') && !user.value?.mobile) {
    methods.push({
      label: 'Mobile',
      value: 'MOBILE',
    })
  }

  return methods
})

const hasPendingMethod = computed(
  () =>
    user.value?.userPendingVerificationMethods?.some(
      (m) =>
        (m.status === 'AWAITING_APPROVAL' || m.status === 'CONFIRMED') && m.method === form.method,
    ) ?? false,
)

const mobileCountryOptions = computed(() => {
  if (!countries.value) return []

  return countries.value
    .map((country) => {
      return {
        label: `${country.name} (${country.dial_code})`,
        value: country.alpha_2,
      }
    })
    .sort((a, b) => {
      return a.label.localeCompare(b.label)
    })
})

const isMobileMethod = computed(() => {
  return form.method === 'MOBILE'
})

const isEmailMethod = computed(() => {
  return form.method === 'EMAIL'
})

const getFormattedMobile = () => {
  const selectedCountry = mobileCountryOptions.value.find((c) => c.value === form.mobileCountry)

  if (!selectedCountry) {
    return null
  }

  const phoneNumber = parsePhoneNumberFromString(form.mobile, form.mobileCountry)

  return phoneNumber?.isValid() ? phoneNumber.number : null
}

const onSubmit = async () => {
  v$.value.$touch()

  if (v$.value.$invalid) {
    return
  }

  isLoading.value = true

  const postData = {
    method: form.method,
  }

  if (isEmailMethod.value) {
    postData.email = form.email
  } else if (isMobileMethod.value) {
    postData.mobile = getFormattedMobile()
  }

  let userPendingVerificationMethod = null

  try {
    await $apiUnauthenticatedRequest
      .post(
        'user-pending-verification-methods',
        $apiUnauthenticatedRequest.configs.post.setData(postData),
      )
      .then(({ data }) => {
        userPendingVerificationMethod = data.id
      })

    await router.push({
      name: 'auth.verification.methods.confirm',
      params: {
        userPendingVerificationMethod: userPendingVerificationMethod,
      },
    })
  } catch (error) {
    const result = await handleErrors(error)
    errors.value = result.validationErrors
  } finally {
    isLoading.value = false
  }
}
</script>
